import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Sheet from "components/sheet/Sheet";

import { ICustomerPaymentMethod } from "api/rpc/2024-04/customer/paymentMethod";
import { ICart } from "redux/reducers/models/cart";
import "./quickChargeTabModal.scss";
import CreditCardTab from "elements/teesheet/creditCard/CreditCardTab";
import Divider from "components/divider";
import PriceDisplay from "./PriceDisplay";
import { displayPercent } from "helpers/Helpers";
import { LocaleCurrencyString } from "helpers/Locale";
import CartLineItemsNew from "../cartLineItemsNew/CartLineItemsNew";
import Callout from "components/callout/Callout";
import { ButtonNew } from "components/buttonNew";

interface IQuickChargeTabModalProps {
  open: boolean;
  customerPaymentMethods: ICustomerPaymentMethod[];
  selectedCardID: number | null;
  tab: ICart;
  manageCard?: boolean;
  onCancel: () => void;
  setSelectedCard: (paymentMethodId: number) => void;
  onOk: () => void;
  onAddCard: () => void;
}

enum QuickChargeTabStep {
  PaymentMethod = "paymentMethod",
  Confirmation = "confirmation",
}

export default function QuickChargeTabModal(props: IQuickChargeTabModalProps) {
  const {
    open,
    customerPaymentMethods,
    selectedCardID,
    tab,
    manageCard = false,
    onCancel,
    onOk,
    setSelectedCard,
    onAddCard,
  } = props;
  const { t } = useTranslation();
  const [step, setStep] = useState(QuickChargeTabStep.PaymentMethod);

  useEffect(() => {
    if (!open) {
      setStep(QuickChargeTabStep.PaymentMethod);
      return;
    }

    if (tab?.payment_method_saved_on_tab && !manageCard) {
      setStep(QuickChargeTabStep.Confirmation);
      setSelectedCard(tab.payment_method_saved_on_tab.id);
      return;
    }

    setStep(QuickChargeTabStep.PaymentMethod);
    if (tab?.payment_method_saved_on_tab) {
      setSelectedCard(tab.payment_method_saved_on_tab.id);
    }
  }, [open, tab?.id, manageCard]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onCancel]);

  const paymentMethods = (() => {
    const saved = tab?.payment_method_saved_on_tab;
    const methods = customerPaymentMethods ?? [];
    if (saved && !methods.some(method => method.id === saved.id)) {
      return [saved, ...methods];
    }
    return methods;
  })();

  const selectedMethod =
    paymentMethods.find(method => method.id === selectedCardID) ??
    (tab?.payment_method_saved_on_tab?.id === selectedCardID ? tab.payment_method_saved_on_tab : undefined);

  const taxLines = (tab?.tax_lines ?? []).map(taxLine => ({
    label: `${taxLine.title} ${displayPercent(taxLine.rate, 3)}`,
    amount: taxLine.price,
  }));

  // Order of pricing is how it appears on the quick charge review.
  const pricingLines = [
    {
      label: t("Subtotal"),
      amount: tab?.subtotal_price,
    },
    ...(taxLines.length > 0 ? taxLines : [{ label: t("Tax"), amount: tab?.total_tax }]),
    {
      label: t("Tip"),
      amount: tab?.total_tip,
      dividerBefore: true,
    },
    ...(Number(tab?.service_fee_total) > 0
      ? [
          {
            label: t("Service Fee"),
            amount: tab?.service_fee_total,
          },
        ]
      : []),
    {
      label: t("Total"),
      amount: tab?.total_price,
    },
  ];

  function isConfirmationStep() {
    return step === QuickChargeTabStep.Confirmation;
  }

  function isPaymentMethodStep() {
    return step === QuickChargeTabStep.PaymentMethod;
  }

  function changeCard() {
    if (isConfirmationStep()) {
      setStep(QuickChargeTabStep.PaymentMethod);
      return;
    }

    onCancel();
  }

  function handleOk() {
    if (manageCard) {
      onOk();
      return;
    } else if (isPaymentMethodStep()) {
      setStep(QuickChargeTabStep.Confirmation);
      return;
    }

    onOk();
  }

  function handleCancel() {
    onCancel();
  }

  const chargeText = `${t("Charge")} ${LocaleCurrencyString({
    currency: tab?.currency ?? "cad",
    amount: tab?.total_price_due,
  })}`;

  const itemCount = tab?.line_items?.length ?? 0;
  const orderDetailSupportText = `${tab?.admin_user?.full_name ?? ""} · ${itemCount} ${t("items")} · ${t(
    "opened",
  )} ${new Date(String(tab?.created_at)).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })}`;

  return (
    <Sheet
      title={step === QuickChargeTabStep.PaymentMethod ? t("Quick charge tab") : t("Charge Tab")}
      supportText={
        step === QuickChargeTabStep.PaymentMethod ? t("Select a payment method to charge") : orderDetailSupportText
      }
      open={open}
      size="small"
      closable
      onClose={onCancel}
      onCancel={handleCancel}
      onOk={handleOk}
      okText={
        step === QuickChargeTabStep.PaymentMethod && !manageCard
          ? t("Continue")
          : step === QuickChargeTabStep.PaymentMethod && manageCard
          ? t("Save")
          : chargeText
      }
      okDisabled={!selectedCardID}
      cancelText={step === QuickChargeTabStep.PaymentMethod ? t("Cancel") : t("Go Back")}
      backDropCancel
    >
      {step === QuickChargeTabStep.PaymentMethod || manageCard ? (
        <>
          {paymentMethods.length > 0 ? (
            paymentMethods.map(method => {
              return (
                <div key={method.id} className="ui-checkbox-group mb-3">
                  <CreditCardTab
                    isSelected={method.id === selectedCardID}
                    card={method}
                    setSelectedCard={setSelectedCard}
                  />
                </div>
              );
            })
          ) : (
            <Callout
              type="warning"
              title={t("No saved payment method")}
              content={t("This customer doesn’t have any saved payment methods yet. Create one to charge this tab.")}
            />
          )}

          {tab?.customer_id ? (
            <ButtonNew type="text" onClick={onAddCard}>
              {t("Add new card")}
            </ButtonNew>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col">
          <div className="mb-4">
            <CartLineItemsNew
              currency={tab?.currency}
              lineItems={tab?.line_items}
              discountLines={tab?.discount_lines}
            />
          </div>

          <div>
            {pricingLines.map(({ label, amount, dividerBefore }, index) => (
              <React.Fragment key={`${label}-${index}`}>
                {dividerBefore ? <Divider /> : null}
                <PriceDisplay label={label} currency={tab?.currency} amount={amount} />
              </React.Fragment>
            ))}
          </div>

          {selectedMethod ? (
            <div className="ui-checkbox-group mt-3">
              <CreditCardTab changeCard isSelected={true} card={selectedMethod} setSelectedCard={changeCard} />
            </div>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}
